import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, Info } from 'lucide-react';

export const EnhancedDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select an option",
  searchable = false,
  multiSelect = false,
  className = "",
  defaultValue = null,
  showTooltips = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Set default value if provided and no current value
  useEffect(() => {
    if (defaultValue && !value && onChange) {
      onChange(defaultValue);
    }
  }, [defaultValue, value, onChange]);

  const filteredOptions = searchable 
    ? options.filter(option => 
        (option.label || option.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.icon?.includes(searchTerm) ||
        option.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setShowTooltip(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (option) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some(v => v.id === option.id);
      
      if (isSelected) {
        onChange(currentValues.filter(v => v.id !== option.id));
      } else {
        onChange([...currentValues, option]);
      }
    } else {
      onChange(option);
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
      setShowTooltip(false);
    }
  };

  const getDisplayValue = () => {
    if (multiSelect) {
      if (!value || value.length === 0) return placeholder;
      if (value.length === 1) return value[0].label || value[0].name;
      return `${value.length} selected`;
    }
    
    if (!value) return placeholder;
    return value.label || value.name || placeholder;
  };

  const isSelected = (option) => {
    if (multiSelect) {
      return value && value.some(v => v.id === option.id);
    }
    return value && value.id === option.id;
  };

  const getOptionDisplay = (option) => {
    return option.label || option.name || option.id;
  };

  const handleOptionHover = (option, event) => {
    if (showTooltips && option.description) {
      setTooltipContent(option.description);
      setTooltipPosition({
        x: event.clientX + 10,
        y: event.clientY - 10
      });
      setShowTooltip(true);
    }
  };

  const handleOptionLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className="flex items-center justify-between w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`dropdown-${Math.random().toString(36).substr(2, 9)}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {value && !multiSelect && value.icon && (
            <span className="text-lg">{value.icon}</span>
          )}
          <span className={`truncate ${!value ? 'text-gray-500' : 'text-gray-900'}`}>
            {getDisplayValue()}
          </span>
          {value && value.description && showTooltips && (
            <Info size={14} className="text-gray-400" />
          )}
        </div>
        <ChevronDown 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          size={20} 
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-gray-500 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    highlightedIndex === index ? 'bg-purple-50' : 'hover:bg-gray-50'
                  } ${isSelected(option) ? 'bg-purple-100' : ''}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={(e) => handleOptionHover(option, e)}
                  onMouseLeave={handleOptionLeave}
                >
                  {option.icon && (
                    <span className="text-lg">{option.icon}</span>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getOptionDisplay(option)}</span>
                      {option.description && showTooltips && (
                        <Info size={12} className="text-gray-400" />
                      )}
                    </div>
                    {option.description && (
                      <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                    )}
                  </div>
                  {isSelected(option) && (
                    <Check className="text-purple-600" size={16} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tooltip */}
      {showTooltip && tooltipContent && (
        <div
          className="fixed z-[60] bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-lg max-w-xs"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateY(-100%)'
          }}
        >
          {tooltipContent}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
}; 